/**
 * SAM-629 + reset-password: who may do what to a user, and what to tell staff when the backend
 * refuses. Pure functions so the rules can be unit-tested against the backend's own rules:
 *
 * - samUserManagement `_authorize` (MANAGE_USERS; self_ok for reset-password; a business-scoped
 *   caller only reaches users of its own business; `admin` targets need a tenant-scoped caller;
 *   `xhinola_admin` targets are never modifiable).
 * - samUserManagement `PUT/DELETE /users/{user_id}/verified-document` (MANAGE_USERS, nobody
 *   verifies their own document, business scope checked server-side by link).
 *
 * These only decide what to SHOW. The server re-checks everything.
 */
import { capabilityScope, MANAGE_USERS, SCOPE_BUSINESS, SCOPE_TENANT } from './capabilities';

export interface Caller {
  groups: string[];
  callerBusinessId: string | null;
  callerSub: string | null;
  email?: string | null;
}

export interface ResetTarget {
  username: string;
  sub?: string | null;
  email?: string | null;
  groups?: string[];
  businessId?: string | null;
  enabled?: boolean;
  status?: string | null;
}

export interface Permission {
  allowed: boolean;
  /** Why not, when `allowed` is false. */
  reason?: string;
}

const lower = (v?: string | null) => (v ? String(v).toLowerCase() : '');

/**
 * Groups that work without a `custom:business_id`: a property manager is scoped by estate, and
 * `business_invitation_all` spans businesses (mirrors the crud `_scope_needs_claim` exemptions).
 */
const GROUPS_WITHOUT_BUSINESS = new Set(['business_property_manager', 'business_invitation_all']);

/**
 * True when assigning this Cognito group needs the account to have a business. `dashboard` counts: the
 * crud confines it to a business like the `business*` groups (read-only) and answers 403 without one.
 */
export const groupNeedsBusiness = (group: string): boolean =>
  group === 'dashboard' || (group.startsWith('business') && !GROUPS_WITHOUT_BUSINESS.has(group));

/** A `custom:business_id` that counts: the attribute is sometimes stored as the text "null". */
export const hasBusinessId = (value: unknown): boolean => {
  if (value === null || value === undefined) return false;
  const text = String(value).trim().toLowerCase();
  return text !== '' && text !== 'null' && text !== 'none' && text !== 'undefined';
};

export const isSameUser = (caller: Caller, target: { username?: string; sub?: string | null; email?: string | null }) => {
  if (caller.callerSub && target.sub && caller.callerSub === target.sub) return true;
  const mine = new Set([lower(caller.callerSub), lower(caller.email)].filter(Boolean));
  return [lower(target.username), lower(target.email)].some((v) => v && mine.has(v));
};

/** Mirrors `_authorize(event, username, self_ok=True, mutating=True)` plus two Cognito states. */
export function canResetPassword(caller: Caller, target: ResetTarget): Permission {
  const scope = capabilityScope(caller.groups, MANAGE_USERS);
  if (isSameUser(caller, target)) {
    // The backend allows it (self_ok) but it would lock the caller out of the session they are using.
    return { allowed: false, reason: 'This is your own account. Use "Forgot password" on the sign-in page.' };
  }
  if (scope === null) return { allowed: false, reason: 'You need user management permission to reset passwords.' };
  if (scope === SCOPE_BUSINESS) {
    if (!caller.callerBusinessId) return { allowed: false, reason: 'You need user management permission to reset passwords.' };
    if (!target.businessId || String(target.businessId) !== String(caller.callerBusinessId)) {
      return { allowed: false, reason: 'You can only reset passwords of your own business users.' };
    }
  }
  const groups = target.groups || [];
  if (groups.includes('xhinola_admin')) return { allowed: false, reason: 'Xhinola accounts cannot be reset from here.' };
  if (groups.includes('admin') && scope !== SCOPE_TENANT) {
    return { allowed: false, reason: 'Only a tenant administrator can reset an administrator.' };
  }
  if (target.enabled === false) return { allowed: false, reason: 'The user is inactive. Activate them first.' };
  if (target.status === 'FORCE_CHANGE_PASSWORD') {
    // Cognito refuses AdminResetUserPassword for a user that never set its own password.
    return { allowed: false, reason: 'The user has not signed in yet, so there is no password to reset.' };
  }
  return { allowed: true };
}

/** Mirrors `_authorize_db_user`: MANAGE_USERS and not the caller's own record. */
export function canManageVerifiedDocument(caller: Caller, targetCognitoId?: string | null): Permission {
  const scope = capabilityScope(caller.groups, MANAGE_USERS);
  if (scope === null || (scope === SCOPE_BUSINESS && !caller.callerBusinessId)) {
    return { allowed: false, reason: 'You need user management permission to verify documents.' };
  }
  if (targetCognitoId && caller.callerSub && targetCognitoId === caller.callerSub) {
    return { allowed: false, reason: 'You cannot verify your own document.' };
  }
  return { allowed: true };
}

// ── errors ────────────────────────────────────────────────────────────────────
interface ApiErrorLike {
  message?: string;
  response?: { status?: number; data?: { error?: string; message?: string; code?: string } | string };
}

const errorBody = (error: unknown) => {
  const e = error as ApiErrorLike;
  const data = e?.response?.data;
  const body = data && typeof data === 'object' ? data : {};
  return {
    status: e?.response?.status,
    code: body.code,
    text: body.error || body.message || (typeof data === 'string' ? data : '') || '',
  };
};

/**
 * API Gateway answers a route that is not deployed with 403 "Missing Authentication Token", and the
 * Lambda's own router with 404 "Route not found". Neither carries our `code`.
 */
export const isRouteNotDeployed = (error: unknown) => {
  const { status, code, text } = errorBody(error);
  if (code) return false;
  if (status === 403 && /missing authentication token/i.test(text)) return true;
  if (status === 404 && (!text || /route not found|not found$/i.test(text)) && !/user/i.test(text)) return true;
  return false;
};

export function resetPasswordErrorMessage(error: unknown): string {
  const { status, code, text } = errorBody(error);
  if (!status) return 'Could not reach the server. Check the connection and try again.';
  if (isRouteNotDeployed(error)) return 'Password reset is not available on this environment yet.';
  if (code === 'forbidden_capability' || status === 403) return 'You are not allowed to reset this user\'s password.';
  if (code === 'not_found' || status === 404) return 'This user no longer exists.';
  if (/no registered\/verified email|no registered|verified email or phone/i.test(text)) {
    return 'The user has no verified email or phone, so Cognito cannot send the reset code.';
  }
  if (/current state/i.test(text)) return 'The user has not signed in yet, so there is no password to reset.';
  if (/disabled/i.test(text)) return 'The user is inactive. Activate them first.';
  if (/limit exceeded|attempt limit|too many/i.test(text)) return 'Too many resets for this user. Try again later.';
  if (status === 400) return text || 'The reset request was not accepted.';
  return 'Could not reset the password. Try again.';
}

export const VERIFIED_DOCUMENT_ERRORS: Record<string, string> = {
  invalid_document_number: 'That number is not valid for the selected document. Check it against the card.',
  unsupported_document: 'Only a cédula or a passport can be recorded.',
  unsupported_country: 'Only Dominican documents are recorded.',
  document_expired: 'The document is expired, so it cannot be used.',
  invalid_expires_at: 'The expiry date is not a valid date.',
  invalid_body: 'The request was not valid. Reload the page and try again.',
  invalid_user_id: 'This user record cannot be verified.',
  self_modification_forbidden: 'You cannot verify your own document.',
  forbidden_capability: 'You are not allowed to verify documents for this user.',
  not_found: 'This user was not found in your property.',
  lookup_failed: 'Could not load the user. Try again.',
  write_failed: 'Could not save the change. Try again.',
};

export function verifiedDocumentErrorMessage(error: unknown): string {
  const { status, code } = errorBody(error);
  if (!status) return 'Could not reach the server. Check the connection and try again.';
  if (isRouteNotDeployed(error)) return 'Document verification is not available on this environment yet.';
  if (code && VERIFIED_DOCUMENT_ERRORS[code]) return VERIFIED_DOCUMENT_ERRORS[code];
  if (status === 403) return VERIFIED_DOCUMENT_ERRORS.forbidden_capability;
  if (status === 404) return VERIFIED_DOCUMENT_ERRORS.not_found;
  return 'Could not save the change. Try again.';
}

// ── verified_document (public view) ───────────────────────────────────────────
/** What `GET /user/{id}` returns once SAM-629 is deployed: never the hash, never who verified. */
export interface VerifiedDocument {
  type: string;
  document?: string;
  country?: string;
  verified_at?: string;
  source?: 'ocr' | 'reception' | string;
  expires_at?: string;
}

export const parseVerifiedDocument = (raw: unknown): VerifiedDocument | null => {
  let v = raw;
  if (typeof v === 'string') {
    try {
      v = JSON.parse(v);
    } catch {
      return null;
    }
  }
  if (!v || typeof v !== 'object' || Array.isArray(v)) return null;
  const obj = v as Record<string, unknown>;
  if (!obj.type) return null;
  return obj as unknown as VerifiedDocument;
};

const DOCUMENT_LABELS: Record<string, string> = { cedula: 'Cédula', passport: 'Passport' };
const COUNTRY_LABELS: Record<string, string> = { DO: 'Dominican' };

export interface VerifiedDocumentSummary {
  documentLabel: string;
  sourceLabel: string;
  grantsDominicanRate: boolean;
  verifiedAt: string | null;
  expiresAt: string | null;
  isExpired: boolean;
}

export function describeVerifiedDocument(v: VerifiedDocument, now: Date = new Date()): VerifiedDocumentSummary {
  const doc = v.document || v.type;
  const country = v.country ? COUNTRY_LABELS[v.country] || v.country : '';
  const documentLabel = [country, (DOCUMENT_LABELS[doc] || doc).toLowerCase()].filter(Boolean).join(' ');
  const expires = v.expires_at ? new Date(`${v.expires_at.slice(0, 10)}T23:59:59Z`) : null;
  return {
    documentLabel: documentLabel.charAt(0).toUpperCase() + documentLabel.slice(1),
    sourceLabel: v.source === 'ocr' ? 'ID scan' : v.source === 'reception' ? 'Reception' : v.source || 'Unknown',
    grantsDominicanRate: String(v.type).toLowerCase() === 'cedula',
    verifiedAt: v.verified_at || null,
    expiresAt: v.expires_at || null,
    isExpired: !!expires && !Number.isNaN(expires.getTime()) && expires < now,
  };
}

/**
 * The roles offered when a person joins a business (SAM-1297): Add team member and the business step of the user
 * drawer. Operator is the level most people need and the one marked by default; it is the Front desk group
 * (`business_invitation`) until the Team v3 operator role lands (decision 30 Oct 2026). Admin is chosen on purpose.
 */
export const BUSINESS_USER_ROLES = [
  { id: 'business_invitation', name: 'Operator', desc: 'Today’s bookings, check-in and walk-in sales. No prices or team' },
  { id: 'business_admin', name: 'Admin', desc: 'Services, prices, bookings, payments and team' },
] as const;

export const DEFAULT_BUSINESS_ROLE = BUSINESS_USER_ROLES[0].id;
